import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { NotificationItem, NotificationPage, NotificationPreferenceProduct } from './notifications.models';

// Gateway /api/notifications wire shapes (InboxItemDo + the preference grid).
interface WireInboxItem {
  id: string;
  title: string;
  body: string;
  link?: { scope?: string | null; id?: string | null; url?: string | null } | null;
  product?: string | null;
  eventType?: string | null;
  isRead?: boolean;
  createdAt?: string | null;
}

interface WirePage {
  items: WireInboxItem[];
  count: number;
  page: number;
  totalPages: number;
}

function toItem(wire: WireInboxItem): NotificationItem {
  return {
    id: wire.id,
    title: wire.title,
    body: wire.body,
    linkUrl: wire.link?.url ?? null,
    product: wire.product ?? null,
    eventType: wire.eventType ?? null,
    isRead: wire.isRead === true,
    createdAt: wire.createdAt ?? null,
  };
}

@Injectable({ providedIn: 'root' })
export class NotificationsApiService {
  private readonly http = inject(HttpClient);

  async getPage(params: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
    product?: string | null;
  }): Promise<NotificationPage | null> {
    try {
      let hp = new HttpParams()
        .set('pagination', JSON.stringify({ limit: params.limit ?? 20, page: params.page ?? 1 }))
        .set('sort', JSON.stringify([{ field: 'createdAt', order: 'desc' }]));
      if (params.unreadOnly) hp = hp.set('unreadOnly', 'true');
      if (params.product) hp = hp.set('product', params.product);
      const wire = await firstValueFrom(this.http.get<WirePage>('/api/notifications', { params: hp }));
      return { items: wire.items.map(toItem), count: wire.count, page: wire.page, totalPages: wire.totalPages };
    } catch {
      return null;
    }
  }

  async getUnreadCount(): Promise<number | null> {
    try {
      const wire = await firstValueFrom(this.http.get<{ unreadCount: number }>('/api/notifications/unread-count'));
      return wire.unreadCount;
    } catch {
      return null;
    }
  }

  async markRead(id: string): Promise<boolean> {
    try {
      await firstValueFrom(this.http.post(`/api/notifications/${encodeURIComponent(id)}/read`, {}));
      return true;
    } catch {
      return false;
    }
  }

  async markUnread(id: string): Promise<boolean> {
    try {
      await firstValueFrom(this.http.post(`/api/notifications/${encodeURIComponent(id)}/unread`, {}));
      return true;
    } catch {
      return false;
    }
  }

  async markAllRead(): Promise<boolean> {
    try {
      await firstValueFrom(this.http.post('/api/notifications/read-all', {}));
      return true;
    } catch {
      return false;
    }
  }

  async getPreferences(): Promise<NotificationPreferenceProduct[] | null> {
    try {
      return await firstValueFrom(this.http.get<NotificationPreferenceProduct[]>('/api/notifications/preferences'));
    } catch {
      return null;
    }
  }

  async setPreference(product: string, eventType: string, emailEnabled: boolean): Promise<boolean> {
    try {
      await firstValueFrom(this.http.post('/api/notifications/preferences', { product, eventType, emailEnabled }));
      return true;
    } catch {
      return false;
    }
  }
}
